/**
 * Glue for the kitchen sink installation's build-time links — the twin of
 * `vanity-assembly/attachVanityLinks`.
 *
 * Emits the auto-links (`emitKitchenSinkLinks`), merges them with whatever
 * the anchor already carries (`mergeLinks`, so a user-drawn link survives a
 * rebuild), and returns a NEW objects array with the result stamped onto the
 * group anchor's `groupCustomization.links`. Never mutates its input.
 */

const { emitKitchenSinkLinks } = require('./emitKitchenSinkLinks');
const { getKitchenSinkAnchor } = require('./fixtureGroup');
const { mergeLinks, reconcileGroupLinks } = require('../group-customization');

/**
 * Attach freshly-emitted auto-links to one kitchen sink group's anchor.
 *
 * @param {object[]} groupObjects — one fixture group's world-space objects.
 * @returns {object[]} a new array with the anchor shallow-cloned, or the
 *   input unchanged when there is no anchor and nothing to record.
 */
function attachKitchenSinkLinks(groupObjects) {
  if (!Array.isArray(groupObjects) || groupObjects.length === 0) {
    return groupObjects;
  }
  const anchor = getKitchenSinkAnchor(groupObjects);
  if (!anchor) return groupObjects;

  const emitted = emitKitchenSinkLinks(groupObjects);
  const existingCz = anchor.groupCustomization || null;
  const existingLinks = existingCz?.links || [];
  const validIds = new Set(groupObjects.map((o) => o?.id).filter(Boolean));
  const merged = mergeLinks(existingLinks, emitted, { validIds });
  // Born verified: rewrite any never-verified offset from this geometry and
  // stamp the rest, moving nothing (layout-relationships §3.2).
  const { links } = reconcileGroupLinks(groupObjects, merged);

  // Nothing emitted and nothing stored → don't stamp an empty
  // groupCustomization onto e.g. a faucet-less bar sink.
  if (links.length === 0 && !existingCz) return groupObjects;

  const updatedAnchor = {
    ...anchor,
    // The anchor stored fact: this module decided who carries the record.
    groupAnchor: true,
    groupCustomization: { ...(existingCz || {}), links },
  };
  return groupObjects.map((o) => (o && o.id === anchor.id ? updatedAnchor : o));
}

/**
 * Attach links for one or more kitchen sink groups inside a full room
 * objects array. Objects outside the named groups pass through untouched and
 * room order is preserved.
 *
 * @param {object[]} allObjects — every object in the room.
 * @param {Iterable<string>} fixtureGroupIds — groups to (re)emit links for.
 * @returns {object[]}
 */
function attachKitchenSinkLinksForGroups(allObjects, fixtureGroupIds) {
  if (!Array.isArray(allObjects) || allObjects.length === 0) return allObjects;
  const groupIds = new Set([...(fixtureGroupIds || [])].filter(Boolean));
  if (groupIds.size === 0) return allObjects;

  const replacements = new Map();
  for (const gid of groupIds) {
    const groupObjects = allObjects.filter((o) => o?.fixtureGroupId === gid);
    if (groupObjects.length === 0) continue;
    const updated = attachKitchenSinkLinks(groupObjects);
    if (updated === groupObjects) continue;
    for (const obj of updated) {
      const original = groupObjects.find((o) => o.id === obj.id);
      if (original !== obj) replacements.set(obj.id, obj);
    }
  }
  if (replacements.size === 0) return allObjects;
  return allObjects.map((o) =>
    o && replacements.has(o.id) ? replacements.get(o.id) : o
  );
}

module.exports = { attachKitchenSinkLinks, attachKitchenSinkLinksForGroups };
